import { Text, List } from "@chakra-ui/react"
import Figure, { PopOverProps } from "@hydrologiq/shared/figures/generic/Figure"
import { pctChange, roundToDP } from "@hydrologiq/common/helpers/math"
import { EnergyPeriod } from "@hydrologiq/common/types/energy/consumption"
import { Emissions as EmissionsType } from "@hydrologiq/gql/graphql"
import { CO2eBasis } from "@hydrologiq/common/types/co2e"
import FigureContent from "@hydrologiq/shared/figures/generic/FigureContent"
import TrendIndicator from "@hydrologiq/shared/figures/generic/TrendIndicator"

export interface EmissionsProps {
  period: EnergyPeriod | "today" | "yesterday"
  emissions?: EmissionsType[]
  error?: string
  loading?: boolean
  title?: string
  simple?: boolean
  isPlaceholder?: boolean
  compact?: boolean
}

function EmissionsFactor({
  period,
  emissions,
  error,
  loading = false,
  title,
  simple = true,
  isPlaceholder,
  compact = true,
}: EmissionsProps) {
  let total = emissions ? 0 : undefined
  let change = 0
  let comparison = 0
  let comparisonType: CO2eBasis | undefined = undefined
  let prevDayTotal: number | undefined = undefined
  const unit = "kg"

  if (emissions && emissions.length > 0) {
    const multipleDays = emissions.length > 1
    let currentDay = emissions[multipleDays ? 1 : 0]
    let previousDay = multipleDays ? emissions[0] : undefined

    if (currentDay?.diesel?.comparison) {
      comparison = currentDay.diesel.comparison.total
      comparisonType = CO2eBasis.HYDROGEN
    }
    if ((comparison === 0 || comparison === undefined) && currentDay?.hydrogen?.comparison) {
      comparison = currentDay.hydrogen.comparison.total
      comparisonType = CO2eBasis.DIESEL
    }

    total = (currentDay.diesel?.total || 0) + (currentDay.hydrogen?.total || 0)
    if (previousDay) {
      prevDayTotal = (previousDay.diesel?.total || 0) + (previousDay.hydrogen?.total || 0)
      change = roundToDP(pctChange(total, prevDayTotal), 1)
    }
  }

  const valid = total !== undefined

  let savings =
    total && comparison !== 0
      ? roundToDP(comparison < total ? pctChange(comparison, total) : pctChange(total, comparison), 0)
      : 0
  let savingsTotal = total && comparison !== 0 ? total - comparison : 0
  if (savingsTotal < 0) savingsTotal *= -1
  if (savings > 0) savings *= -1

  const popover: undefined | PopOverProps = {
    title: "Information",
    content: (
      <>
        <Text>This number includes CO2 emissions from:</Text>
        <List.Root width={"100%"}>
          <List.Item>Fuel production</List.Item>
          <List.Item>Fuel transportation</List.Item>
          <List.Item>Tailpipe / end-use</List.Item>
        </List.Root>
      </>
    ),
  }
  return (
    <Figure
      heading={title ?? "CO2 Emissions"}
      isPlaceholder={!valid || isPlaceholder}
      error={error}
      loading={loading}
      popover={popover}
      compact={compact}
    >
      {total !== undefined && (
        <FigureContent.Root compact={compact}>
          <FigureContent.SubText link={<TrendIndicator change={change} />} compact={compact}>{period === "today" ? "so far today" : period}</FigureContent.SubText>
          <FigureContent.Value compact={compact}>
            {roundToDP(total, 2)} {unit}
          </FigureContent.Value>
        </FigureContent.Root>

      )}
    </Figure>
  )
}

export default EmissionsFactor
